/**
 * Éditeur visuel d'un circuit — refonte UX, chantier 5 (maquette 6, validée le 24/09).
 *
 * Les étapes sur une ligne, dans l'ordre ; les flèches vers l'avant en arcs
 * au-dessus, les retours et abandons en arcs POINTILLÉS au-dessous (disposition
 * qui marche pour n'importe quel circuit). Chaque flèche est un vrai bouton
 * (clavier, lecteur d'écran) ; un clic ouvre le panneau des QUATRE QUESTIONS :
 * qui peut le faire · ce qu'il faut fournir · qui prévenir · ce qui se déclenche,
 * avec une phrase qui résume la règle. On modifie la flèche SANS la recréer
 * (historique des dossiers préservé).
 */
import { useMemo, useState } from 'react';
import { Button, Badge } from '@/components/ui';
import type { WorkflowDefinition } from '@/lib/api';

type Def = WorkflowDefinition;
type Fleche = Def['transitions'][number];
const ROLES: Record<string, string> = { agent: 'un agent ou plus', manager: 'un manager ou plus', admin: 'un administrateur' };
const SERVICES: Record<string, string> = {
  service_technique: 'du service technique', grc: 'de l’accueil (GRC)', rh: 'des ressources humaines', urbanisme: 'de l’urbanisme',
  comptable: 'de la comptabilité', scolaire_cantine: 'du scolaire', associations: 'des associations', petite_enfance: 'de la petite enfance',
};
const LARGEUR = 176; const NOEUD_L = 148; const NOEUD_H = 52;

export function phraseFleche(t: Fleche, de?: string, vers?: string): string {
  const qui = `${ROLES[t.roleMinimum] ?? t.roleMinimum}${t.serviceRequis ? ` ${SERVICES[t.serviceRequis] ?? t.serviceRequis}` : ''}`;
  let p = `Quand ${qui} choisit « ${t.libelle} »${t.pieceRequise ? ' (avec une pièce jointe)' : ''}, le dossier passe de « ${de ?? '?'} » à « ${vers ?? '?'} »`;
  const suites: string[] = [];
  if (t.evenementNotification) suites.push('les personnes concernées reçoivent une notification');
  if (t.envoiConfig) suites.push(`un ${t.envoiConfig.canal === 'email' ? 'e-mail' : t.envoiConfig.canal === 'sms' ? 'SMS' : 'message interne'} est préparé${t.envoiConfig.validationRequise ? ', relu et validé avant envoi' : ''}`);
  if (t.recrediteBudget) suites.push('le montant réservé est rendu au budget');
  if (suites.length) p += ` ; ${suites.join(' ; ')}`;
  return `${p}.`;
}

export function CircuitEditeurVisuel({ definition, onModifier, onConfigurerEnvoi }: {
  definition: Def;
  onModifier: (transitionId: string, body: { libelle?: string; roleMinimum?: 'agent' | 'manager' | 'admin'; serviceRequis?: string | null; pieceRequise?: boolean }) => Promise<void>;
  onConfigurerEnvoi: (transitionId: string) => void;
}) {
  const etats = useMemo(() => [...definition.etats].sort((a, b) => a.ordre - b.ordre), [definition.etats]);
  const idx = useMemo(() => new Map(etats.map((e, i) => [e.id, i])), [etats]);
  const [selId, setSelId] = useState<string | null>(null);
  const sel = definition.transitions.find((t) => t.id === selId) ?? null;
  const [brouillon, setBrouillon] = useState<{ libelle: string; roleMinimum: string; serviceRequis: string; pieceRequise: boolean } | null>(null);

  // Géométrie : arcs au-dessus (avant) / au-dessous (retour), hauteur selon l'écart.
  const fleches = definition.transitions.map((t, k) => {
    const a = idx.get(t.deEtatId) ?? 0; const b = idx.get(t.versEtatId) ?? 0;
    const avant = b > a; const ecart = Math.abs(b - a);
    const x1 = a * LARGEUR + NOEUD_L / 2; const x2 = b * LARGEUR + NOEUD_L / 2;
    const h = 34 + ecart * 26 + (k % 3) * 8;
    return { t, avant, x1, x2, h, mx: (x1 + x2) / 2 };
  });
  const hautMax = Math.max(60, ...fleches.filter((f) => f.avant).map((f) => f.h)) + 26;
  const basMax = Math.max(40, ...fleches.filter((f) => !f.avant).map((f) => f.h)) + 26;
  const yNoeud = hautMax; const largeur = Math.max(1, etats.length) * LARGEUR; const hauteur = hautMax + NOEUD_H + basMax;

  function choisir(t: Fleche) {
    setSelId(t.id);
    setBrouillon({ libelle: t.libelle, roleMinimum: t.roleMinimum, serviceRequis: t.serviceRequis ?? '', pieceRequise: t.pieceRequise });
  }
  const nom = (id: string) => etats.find((e) => e.id === id)?.libelle;

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_380px]">
      <div className="overflow-x-auto rounded-r-xl border border-border/60 bg-bg-2/40 p-3">
        <p className="mb-2 text-xs text-ink-muted">Cliquez une flèche pour la régler. Pointillés : retours et abandons.</p>
        <div className="relative" style={{ width: largeur, height: hauteur }}>
          <svg width={largeur} height={hauteur} className="absolute inset-0" aria-hidden="true">
            <defs>
              <marker id="fl" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto"><path d="M0 0 10 5 0 10z" fill="currentColor" /></marker>
            </defs>
            {fleches.map(({ t, avant, x1, x2, h }) => {
              const y = avant ? yNoeud : yNoeud + NOEUD_H; const c = avant ? y - h : y + h;
              const actif = t.id === selId;
              return (
                <path key={t.id} d={`M${x1} ${y} C ${x1} ${c}, ${x2} ${c}, ${x2} ${y}`} fill="none"
                  className={actif ? 'text-tech' : 'text-ink-subtle'} stroke="currentColor" strokeWidth={actif ? 2.5 : 1.5}
                  strokeDasharray={avant ? undefined : '5 4'} markerEnd="url(#fl)" />
              );
            })}
          </svg>
          {etats.map((e, i) => (
            <div key={e.id} className={`absolute flex items-center justify-center rounded-r-lg border px-2 text-center text-xs font-medium ${e.estFinal ? 'border-success/50 bg-success/10 text-ink' : e.estInitial ? 'border-tech/50 bg-tech/10 text-ink' : 'border-border bg-surface text-ink'}`}
              style={{ left: i * LARGEUR, top: yNoeud, width: NOEUD_L, height: NOEUD_H }}>{e.libelle}</div>
          ))}
          {fleches.map(({ t, avant, mx, h }) => (
            <button key={t.id} type="button" onClick={() => choisir(t)} aria-pressed={t.id === selId}
              aria-label={`Flèche ${t.libelle}, de ${nom(t.deEtatId)} à ${nom(t.versEtatId)}`}
              className={`absolute max-w-[150px] -translate-x-1/2 -translate-y-1/2 truncate rounded-r-md border px-2 py-1 text-2xs font-medium ${t.id === selId ? 'border-tech bg-tech/20 text-ink' : 'border-border bg-surface text-ink hover:border-tech/50'}`}
              style={{ left: mx, top: avant ? yNoeud - h * 0.75 : yNoeud + NOEUD_H + h * 0.75 }}>
              {t.libelle}
            </button>
          ))}
        </div>
      </div>

      <aside aria-label="Réglage de la flèche" className="rounded-r-xl border border-border/60 bg-bg-2/60">
        {!sel || !brouillon ? (
          <p className="p-4 text-sm text-ink-muted">Choisissez une flèche pour voir qui peut la franchir, ce qu’il faut fournir, qui est prévenu et ce qui se déclenche.</p>
        ) : (
          <div className="divide-y divide-border/60">
            <div className="p-4">
              <p className="text-2xs text-ink-subtle">Flèche sélectionnée</p>
              <p className="text-base font-semibold text-ink">{sel.libelle}</p>
              <p className="text-xs text-ink-muted">{nom(sel.deEtatId)} → {nom(sel.versEtatId)}</p>
              <p className="mt-3 rounded-r-lg border border-tech/30 bg-tech/10 p-3 text-sm leading-relaxed text-ink">{phraseFleche(sel, nom(sel.deEtatId), nom(sel.versEtatId))}</p>
            </div>
            <div className="space-y-2 p-4">
              <p className="text-2xs font-semibold text-ink-subtle">Qui peut le faire</p>
              <label className="block text-xs text-ink">Libellé du bouton
                <input className="mt-1 h-9 w-full rounded-r-md border border-border bg-surface px-2 text-sm" value={brouillon.libelle} maxLength={100}
                  onChange={(e) => setBrouillon({ ...brouillon, libelle: e.target.value })} /></label>
              <div className="grid grid-cols-2 gap-2">
                <label className="block text-xs text-ink">Rôle minimum
                  <select className="mt-1 h-9 w-full rounded-r-md border border-border bg-surface px-2 text-sm" value={brouillon.roleMinimum}
                    onChange={(e) => setBrouillon({ ...brouillon, roleMinimum: e.target.value })}>
                    <option value="agent">Agent</option><option value="manager">Manager</option><option value="admin">Administrateur</option>
                  </select></label>
                <label className="block text-xs text-ink">Service
                  <select className="mt-1 h-9 w-full rounded-r-md border border-border bg-surface px-2 text-sm" value={brouillon.serviceRequis}
                    onChange={(e) => setBrouillon({ ...brouillon, serviceRequis: e.target.value })}>
                    <option value="">Tous</option>{Object.entries(SERVICES).map(([k, v]) => <option key={k} value={k}>{v.replace(/^(du|de l’|de la|des) /, '')}</option>)}
                  </select></label>
              </div>
            </div>
            <div className="p-4">
              <p className="mb-2 text-2xs font-semibold text-ink-subtle">Ce qu’il faut fournir</p>
              <label className="flex items-center gap-2 text-sm text-ink"><input type="checkbox" checked={brouillon.pieceRequise}
                onChange={(e) => setBrouillon({ ...brouillon, pieceRequise: e.target.checked })} /> Une pièce jointe est exigée</label>
            </div>
            <div className="space-y-2 p-4">
              <p className="text-2xs font-semibold text-ink-subtle">Qui prévenir</p>
              {sel.evenementNotification ? <Badge variant="info" size="sm">Notification : {sel.evenementNotification}</Badge> : <p className="text-xs text-ink-muted">Pas de notification automatique.</p>}
              <p className="text-xs text-ink">{sel.envoiConfig ? `Envoi préparé par ${sel.envoiConfig.canal === 'email' ? 'e-mail' : sel.envoiConfig.canal === 'sms' ? 'SMS' : 'message interne'}${sel.envoiConfig.validationRequise ? ', relu avant envoi' : ''}.` : 'Aucun envoi préparé.'}</p>
              <Button size="sm" variant="ghost" onClick={() => onConfigurerEnvoi(sel.id)}>{sel.envoiConfig ? 'Modifier l’envoi' : 'Préparer un envoi'}</Button>
            </div>
            <div className="p-4">
              <p className="mb-2 text-2xs font-semibold text-ink-subtle">Ce qui se déclenche</p>
              <p className="text-xs text-ink">{sel.recrediteBudget ? 'Le montant réservé est rendu au budget du secteur.' : 'Rien d’autre.'}</p>
            </div>
            <div className="p-4">
              <Button size="sm" variant="primary" disabled={brouillon.libelle.trim().length === 0}
                onClick={() => void onModifier(sel.id, { libelle: brouillon.libelle.trim(), roleMinimum: brouillon.roleMinimum as 'agent' | 'manager' | 'admin', serviceRequis: brouillon.serviceRequis || null, pieceRequise: brouillon.pieceRequise })}>
                Enregistrer la flèche
              </Button>
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}
